import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Sentry } from '../../utils/sentry.ts';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
          <div className="bg-[var(--bg-secondary)] rounded-lg p-8 max-w-md w-full text-center">
            <div className="text-[var(--danger)] text-5xl mb-4">!</div>
            <h1 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Something went wrong</h1>
            <p className="text-[var(--text-muted)] mb-4">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            {this.state.error && (
              <details className="text-left mb-4">
                <summary className="text-[var(--text-muted)] cursor-pointer text-sm">
                  Error details
                </summary>
                <pre className="mt-2 p-2 bg-[var(--bg-primary)] rounded text-xs text-[var(--danger)] overflow-auto">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div className="flex gap-2 justify-center">
              <button
                onClick={this.handleRetry}
                className="px-4 py-2 bg-[var(--accent)] text-[var(--text-inverse)] rounded hover:bg-[var(--accent-hover)] transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-[var(--bg-hover)] text-[var(--text-primary)] rounded hover:bg-[var(--bg-active)] transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
